import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { APISetting } from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import { gsap } from "gsap";
import { Sprite, Texture } from "pixi.js";

import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { getRandomBetween, getRandomElement } from "../../../../abstracts/utils/JSUtils";
import { fadeAndStopSound, playSound } from "../../../../abstracts/utils/SoundUtils";
import { m5StoneExplodeSounds } from "../../../assetsLoader/ApocalypseAssetsNames";
import { fastSpinDelay, symbolConfig } from "../../../config/ApocalypseConfig";
import { ApocalypseSymbolNames } from "../../../types/ApocalypseEnums";
import { ApocalypseSymbol } from "./ApocalypseSymbol";

export class ApocalypseBlankSymbol extends ApocalypseSymbol {
    protected specialTextureId: number;

    constructor() {
        super(ApocalypseSymbolNames.BLANK);
    }

    override getLandingAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.to({}, {});
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        return gsap.to({}, {});
    }

    public getStopWinAnimation(): gsap.core.Tween {
        return this.mainSpine.getSpineTween(`Normal`);
    }

    public getFeatureAnimation(featureNumber: number = 1): gsap.core.Tween | gsap.core.Timeline {
        const explodeSound = getRandomElement(m5StoneExplodeSounds);

        return this.mainSpine.getSpineTween(
            `mp${featureNumber}_feature`,
            false,
            () => {
                if (featureNumber === 5) {
                    if (!SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN)) {
                        gsap.delayedCall(fastSpinDelay, () => playSound({ name: explodeSound }));
                    } else {
                        playSound({ name: explodeSound });
                    }
                }
                this.removeChild(this.mainTextureSprite);
                this.addChild(this.mainSpine);
            },
            null,
            () => {
                if (featureNumber === 5) {
                    gsap.delayedCall(0.5, () => {
                        fadeAndStopSound({
                            name: explodeSound,
                            from: 1,
                            to: 0,
                            duration: 0.5,
                        });
                    });
                }
                this.removeChild(this.mainSpine);
                this.addChild(this.mainTextureSprite);
            }
        );
    }

    public override reset() {
        super.reset();

        this.hasSpecialTexture = false;
    }

    protected override generateSymbol() {
        this.mainSpine = new AbstractSpine(`spine/Apocalypse/blank.json`);

        this.mainSpineDefaultXPosition = symbolConfig.symbolWidth / 2;
        this.mainSpineDefaultYPosition = symbolConfig.symbolHeight / 2;

        this.mainTextureDefaultName = `Apocalypse/blank_1`;
        this.mainTextureSprite = new Sprite(Texture.from(this.mainTextureDefaultName));
        this.mainTextureSprite.name = `mainTextureSprite`;
        this.mainTextureSprite.scale.set(0.5);
        this.mainTextureSprite.alpha = 1;

        this.mainTextureDefaultXPosition =
            symbolConfig.symbolWidth / 2 - this.mainTextureSprite.width / 2;
        this.mainTextureDefaultYPosition =
            symbolConfig.symbolHeight - this.mainTextureSprite.height;

        this.addChild(this.mainTextureSprite);

        this.hasSpecialTexture = false;

        this.mainSpine.x = this.mainSpineDefaultXPosition;
        this.mainSpine.y = this.mainSpineDefaultYPosition;

        this.mainTextureSprite.x = this.mainTextureDefaultXPosition;
        this.mainTextureSprite.y = this.mainTextureDefaultYPosition;
    }

    public set hasSpecialTexture(value: boolean) {
        if (value) {
            this.specialTextureId = getRandomBetween(5, 10, true);
            this.mainTextureDefaultName = `Apocalypse/blank_${this.specialTextureId}`;
        } else {
            this.specialTextureId = getRandomBetween(1, 4, true);
            this.mainTextureDefaultName = `Apocalypse/blank_${this.specialTextureId}_full`;
        }

        this.mainTextureSprite.texture = Texture.from(this.mainTextureDefaultName);

        this.mainSpine.setSkinByName(`blank_${this.specialTextureId}`);

        this.mainSpine.getSpineTween(`Normal`);
    }

    protected getSpinTextureName(): string {
        let newTextureId = this.specialTextureId;
        if (newTextureId === 6 || newTextureId === 8 || newTextureId === 10) {
            newTextureId--;
        }
        return `Apocalypse/blank_spin_${newTextureId}`;
    }

    public override resetOnStartSpin() {
        super.resetOnStartSpin();
    }
}
